import type { AdditionalFundsRequiredDto } from '@/api/backend/public';
import type { ApplicationDto, UtmMetricDto } from '@/api/backend/users/applications';
import { mapFromApi } from '@/mappers';
import type { Applicant, ApplicantFinancials, ApplicantId, Financials, LoanApplication, LoanTypeEnum } from '@/types';

const mapFinancials = (applicantId: string, application: ApplicationDto): Financials => {
  return {
    income: mapFromApi.income(applicantId, application),
    expenses: mapFromApi.expenses(applicantId, application),
    assets: mapFromApi.assets(applicantId, application),
    liabilities: mapFromApi.liabilities(applicantId, application),
  };
};

export const application = (data: ApplicationDto): LoanApplication => {
  const applicants = data.applicants
    .map(mapFromApi.applicant)
    .sort((applicant) => (applicant.type === 'primary' ? -1 : 1));

  // The backend marks an applicant declared (isApplicantDeclared) once their I&E/declaration is
  // submitted. The application-level declaredApplicantIds array isn't always returned, so union it
  // with the per-applicant flags — this is the authoritative "submitted → read-only" signal every
  // flow relies on.
  const declaredPerApplicant = (data.applicants ?? [])
    .filter((applicant) => applicant.isApplicantDeclared)
    .map((applicant) => applicant.id)
    .filter((id): id is string => !!id);

  const declaredApplicantIds: ApplicantId[] = [
    ...new Set([...(data.declaredApplicantIds ?? []), ...declaredPerApplicant]),
  ];
  const financials = applicants.reduce((obj: Record<string, Financials>, applicant: Applicant) => {
    obj[applicant.id] = mapFinancials(applicant.id, data);
    return obj;
  }, {} as ApplicantFinancials);

  const erhlDetails = data.erhlDetails;
  // Resolve the exit strategy from the active value source (manual override wins, else application value)
  const exitStrategy =
    erhlDetails?.exitStrategyUsed === 'manual_value'
      ? erhlDetails?.exitStrategyManualValue
      : erhlDetails?.exitStrategyApplicationValue;

  return {
    settlementDate: data.settlementDate,
    exitStrategy: exitStrategy as LoanApplication['exitStrategy'],
    exitStrategyDetails: data.exitStrategyDetails,
    additionalFundsRequired: (data.additionalFundsRequired ?? {}) as unknown as AdditionalFundsRequiredDto,
    applicationId: data.id ?? '',
    userType: 'Secondary', // ???
    actionType: 'updateExisting',
    loanType: data.loanType as LoanTypeEnum,
    applicants,
    declaredApplicantIds,
    borrowerGroups: mapFromApi.borrowerGroups(data.borrowers),
    incomingProperties: data.incomingProperties?.map(mapFromApi.incomingProperty) ?? [],
    outgoingProperties: data.outgoingProperties?.map(mapFromApi.outgoingProperty) ?? [],
    supportingProperties: data.supportingProperties?.map(mapFromApi.supportingProperty) ?? [],
    propertiesOwnership: mapFromApi.propertyOwnership(data.propertiesOwnership ?? []),
    loanDetails: mapFromApi.additionalFunds(data),
    financials,
    endDebt: data.calculatedValue?.endDebt ?? 0,
    netSurplus: (data.calculatedValue as { netSurplus?: number } | undefined)?.netSurplus ?? 0,
    verificationCode: '',
    brokerId: data.brokerId ?? '',
    structuralVersionNumber: data.structuralVersionNumber ?? 0,
    summaryNotes: data.summaryNotes ?? '',
    utmMetrics: {} as UtmMetricDto,
    solicitor: data.solicitor,
    domainVariables: data.domainVariables ?? [],
    productType: data.productType,
    lendingProduct: data.lendingProduct,
  };
};
